import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import {
	Table,
	Button,
	message,
	Layout,
	ConfigProvider,
	Space,
	Tag,
	Descriptions,
	Typography,
	Tabs,
} from "antd";
import locale from "antd/lib/locale/ko_KR";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import PageHeader from "../../common/PageHeader";

const { Content } = Layout;
const { Text } = Typography;

const PAYMENT_TABS = [
	{ key: "카드", label: "카드" },
	{ key: "계좌이체", label: "계좌이체" },
];

const currencyLabel = (c) => (c === "USD" ? "달러" : "원");

const lineColumns = [
	{
		title: "통화",
		dataIndex: "currency",
		key: "currency",
		width: 72,
		render: (c) => currencyLabel(c),
	},
	{
		title: "적요",
		dataIndex: "subject",
		key: "subject",
		ellipsis: true,
	},
	{
		title: "금액",
		dataIndex: "amount",
		key: "amount",
		width: 120,
		align: "right",
		render: (v, row) =>
			`${Number(v).toLocaleString("ko-KR")} ${currencyLabel(row.currency)}`,
	},
	{
		title: "비고",
		dataIndex: "description",
		key: "description",
		ellipsis: true,
		render: (t) => t || "—",
	},
];

async function downloadVoucherAttachment(voucherId, attachmentId, filename) {
	const res = await useAxios.get(
		`utils/cash-disbursement/${voucherId}/attachments/${attachmentId}/download/`,
		{
			responseType: "blob",
			headers: { Accept: "application/octet-stream" },
		},
	);
	const blob = new Blob([res.data]);
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename || "download";
	document.body.appendChild(a);
	a.click();
	a.remove();
	window.URL.revokeObjectURL(url);
}

const AccountingApprovedCashDisbursementList = () => {
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [paymentTab, setPaymentTab] = useState("카드");

	const fetchList = useCallback(async () => {
		setLoading(true);
		try {
			const res = await useAxios.get(
				"utils/cash-disbursement/accounting/",
			);
			setRows(res.data || []);
		} catch {
			message.error("목록을 불러오지 못했습니다.");
			setRows([]);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchList();
	}, [fetchList]);

	const filteredRows = useMemo(
		() =>
			(rows || []).filter(
				(r) => (r.payment_method || "카드") === paymentTab,
			),
		[rows, paymentTab],
	);

	const columns = [
		{
			title: "작성일",
			dataIndex: "document_date",
			key: "document_date",
			width: 108,
		},
		{
			title: "작성자",
			dataIndex: "prepared_by_name",
			key: "prepared_by_name",
			width: 88,
			ellipsis: true,
		},
		{
			title: "적요",
			dataIndex: "lines",
			key: "lines",
			ellipsis: true,
			render: (lines) =>
				(lines || []).map((l) => l.subject).join(", "),
		},
		{
			title: "합계(원)",
			dataIndex: "total_amount_krw",
			key: "total_amount_krw",
			width: 112,
			align: "right",
			render: (v) =>
				v != null && v !== ""
					? `${Number(v).toLocaleString("ko-KR")} 원`
					: "—",
		},
		{
			title: "담당자",
			dataIndex: "person_in_charge",
			key: "person_in_charge",
			width: 88,
		},
		{
			title: "결제",
			dataIndex: "payment_method",
			key: "payment_method",
			width: 88,
			render: (pm) => <Tag>{pm || "카드"}</Tag>,
		},
	];

	const expandedRowRender = (record) => (
		<div
			style={{
				padding: "8px 0 16px",
				maxWidth: "100%",
			}}>
			<Descriptions
				bordered
				size="small"
				column={{ xs: 1, sm: 1, md: 2 }}
				style={{ marginBottom: 16 }}>
				<Descriptions.Item label="일자">
					{record.document_date}
				</Descriptions.Item>
				<Descriptions.Item label="작성일시">
					{record.created_at
						? dayjs(record.created_at).format(
								"YYYY-MM-DD HH:mm:ss",
							)
						: "—"}
				</Descriptions.Item>
				<Descriptions.Item label="작성자" span={2}>
					{record.prepared_by_name || "—"}
				</Descriptions.Item>
				<Descriptions.Item label="소속 팀" span={2}>
					{record.team_in_charge}
				</Descriptions.Item>
				<Descriptions.Item label="담당자" span={2}>
					{record.person_in_charge}
				</Descriptions.Item>
				<Descriptions.Item label="결제방식">
					{record.payment_method || "카드"}
				</Descriptions.Item>
				<Descriptions.Item label="계좌번호">
					{record.payment_method === "계좌이체" &&
					(record.bank_account_number || "").trim()
						? record.bank_account_number
						: "—"}
				</Descriptions.Item>
				<Descriptions.Item label="합계(원화 환산)" span={2}>
					{record.total_amount_krw != null &&
					record.total_amount_krw !== ""
						? `${Number(record.total_amount_krw).toLocaleString("ko-KR")} 원`
						: "—"}
				</Descriptions.Item>
			</Descriptions>
			<Text
				strong
				style={{ display: "block", marginBottom: 8 }}>
				적요 · 금액
			</Text>
			<Table
				size="small"
				pagination={false}
				rowKey={(r) =>
					r.id ?? `${r.subject}-${r.amount}`
				}
				dataSource={record.lines || []}
				columns={lineColumns}
				style={{ marginBottom: 16 }}
			/>
			<Text
				strong
				style={{ display: "block", marginBottom: 8 }}>
				영수증 첨부
			</Text>
			{(record.attachments || []).length === 0 ? (
				<Text type="secondary">첨부 파일 없음</Text>
			) : (
				<Space
					direction="vertical"
					size={4}
					align="start">
					{(record.attachments || []).map((att) => (
						<Button
							key={att.id}
							type="link"
							size="small"
							style={{
								padding: 0,
								height: "auto",
							}}
							onClick={() =>
								downloadVoucherAttachment(
									record.id,
									att.id,
									att.original_filename,
								).catch(() =>
									message.error(
										"파일을 받지 못했습니다.",
									),
								)
							}>
							{att.original_filename}
						</Button>
					))}
				</Space>
			)}
		</div>
	);

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="지출결의서(회계) · 승인 건" />
			<Layout style={{ minHeight: "100vh" }}>
				<Content className="app-page-content">
					<div
						className="page"
						style={{
							maxWidth: 980,
							margin: "0 auto",
							width: "100%",
						}}>
						<PageHeader title="지출결의서(회계) · 승인 건" />
						<div className="surface-card">
							<Tabs
								activeKey={paymentTab}
								onChange={setPaymentTab}
								items={PAYMENT_TABS.map(({ key, label }) => ({
									key,
									label,
								}))}
								style={{ marginBottom: 16 }}
							/>
							<Table
								rowKey="id"
								columns={columns}
								dataSource={filteredRows}
								loading={loading}
								scroll={{ x: 880 }}
								sticky={{ offsetHeader: 64 }}
								expandable={{
									expandedRowRender,
									rowExpandable: () => true,
									expandRowByClick: true,
								}}
							/>
						</div>
					</div>
				</Content>
			</Layout>
		</ConfigProvider>
	);
};

export default AccountingApprovedCashDisbursementList;
